import { cn } from "@/lib/utils";

import type { ToolPreviewVariant } from "./previews/types";

interface ToolPreviewShellProps {
  variant: ToolPreviewVariant;
  children: React.ReactNode;
  className?: string;
}

/**
 * Figma preview-shell coords (Frame 2147225298), measured from the card border.
 *
 * Sub-@lg shells use **right-pinned** negative offsets so the panel stays anchored
 * to the bottom-right as card width grows (fixed `left` leaves a gap on md/lg).
 *
 * | Card width   | Variant | Anchor (Figma) | Bleed (right) |
 * |--------------|---------|----------------|---------------|
 * | ~281 (xs)    | all     | left 57        | -19px (~-7%)  |
 * | ~371 (3-col) | compact | left 145       | -17px (~-5%)  |
 * | ~566 (@lg+)  | large   | left 310       | -135px        |
 */
const SUBLG_SHELL = "top-[120px] left-auto right-[-19px] h-[181px] w-[243px]";

/** Desktop 3-col compact cards (~371px) — tighter bleed per Figma. */
const COMPACT_MID_SHELL = "card-mid:top-[118px] card-mid:right-[-17px]";

const LARGE_LG_SHELL =
  "@lg:top-[58px] @lg:left-[310px] @lg:right-auto @lg:h-[291px] @lg:w-[391px]";

/**
 * White bordered preview panel that bleeds off the card bottom-right (Figma toolkit cards).
 * Positioned from the card border box — text padding lives on the title block, not here.
 */
export function ToolPreviewShell({
  variant,
  children,
  className,
}: ToolPreviewShellProps) {
  const isLarge = variant === "large";

  return (
    <div
      aria-hidden
      className={cn(
        "border-grey-100 pointer-events-none absolute overflow-hidden rounded-tl-2xl border-t border-l bg-white",
        isLarge
          ? cn(SUBLG_SHELL, LARGE_LG_SHELL)
          : cn(SUBLG_SHELL, COMPACT_MID_SHELL),
        className
      )}
    >
      {children}
    </div>
  );
}
